/**
 * Static master data for hotel filters — meals & accommodation types.
 *
 * Backend `getMeals()` / `getTypes()` are still mocked, so we use canonical
 * BM_MEALS / BM_HOTEL_TYPES from the API contract (hotel-booking-api bm.types.ts)
 * as the source of truth for filter options until the real endpoints ship.
 *
 * When the real API endpoints become available, replace static labels with
 * fetched translations and drop the keyword heuristics in favor of mealType codes.
 */

// -----------------------------------------------------------------------
// Meal plan options (BM_MEALS)
// -----------------------------------------------------------------------

export interface MealOption {
  code: string;
  label: string;
}

/** Per-meal localised labels for unit tests / deterministic rendering. */
const MEAL_LABELS: Record<string, Record<string, string>> = {
  RO: { es: 'Solo alojamiento', en: 'Room only', pt: 'Somente alojamento' },
  BF: { es: 'Desayuno', en: 'Breakfast', pt: 'Café da manhã' },
  HB: { es: 'Media pensión', en: 'Half board', pt: 'Meia pensão' },
  FB: { es: 'Pensión completa', en: 'Full board', pt: 'Pensão completa' },
  AI: { es: 'Todo incluido', en: 'All inclusive', pt: 'Tudo incluído' },
  OT: { es: 'Otros', en: 'Others', pt: 'Outros' },
};

/**
 * Spanish-first meal option list for the filter UI.
 * “OT” intentionally excluded — it represents catch-all/unknown meal plans
 * and provides no filtering signal.
 */
export function getMealOptions(): MealOption[] {
  return [
    { code: 'RO', label: 'Solo alojamiento' },
    { code: 'BF', label: 'Desayuno' },
    { code: 'HB', label: 'Media pensión' },
    { code: 'FB', label: 'Pensión completa' },
    { code: 'AI', label: 'Todo incluido' },
    // OT excluded — "Others" doesn't narrow results
  ];
}

/**
 * Localised meal label for unit tests and snapshots.
 * Used only in test harnesses; production renderers prefer the Spanish label.
 */
export function getMealLabel(code: string, locale = 'es'): string {
  return MEAL_LABELS[code]?.[locale] ?? code;
}

/**
 * Keyword heuristics for matching raw provider meal strings (e.g. "Desayuno
 * incluido", "Room only rate") to canonical BM meal codes.
 *
 * These are deliberately loose and Spanish-first. When the backend exposes
 * `mealType` codes on `RateFeature`, replace this with exact code matching.
 */
const MEAL_KEYWORDS: Record<string, RegExp[]> = {
  RO: [/room\s*only/i, /s[oó]lo\s*alojamiento/i, /sem\s*caf[ée]/i],
  BF: [/desayuno/i, /breakfast/i, /caf[ée]\s*da\s*manh[ãa]/i],
  HB: [/media\s*pensi[oó]n/i, /half\s*board/i, /meia\s*pens[ãa]o/i],
  FB: [/pensi[oó]n\s*completa/i, /full\s*board/i, /pens[ãa]o\s*completa/i],
  AI: [/todo\s*incluido/i, /all\s*inclusive/i, /tudo\s*inclu[íi]do/i],
  OT: [/otros/i, /others/i, /outros/i],
};

/**
 * Returns all BM meal codes that match the given raw provider meal text.
 * Returns empty array when no keyword matches — the meal is unknown/unmatched.
 */
export function matchMealCodes(mealText: string): string[] {
  const matched: string[] = [];
  for (const [code, patterns] of Object.entries(MEAL_KEYWORDS)) {
    if (patterns.some((regex) => regex.test(mealText))) {
      matched.push(code);
    }
  }
  return matched;
}

// -----------------------------------------------------------------------
// Accommodation / hotel type options (BM_HOTEL_TYPES)
// -----------------------------------------------------------------------

export interface HotelTypeOption {
  id: number;
  label: string;
}

/** Spanish-first static list (ids 1..22 per API contract). */
export function getHotelTypeOptions(): HotelTypeOption[] {
  return [
    { id: 1, label: 'Hoteles' },
    { id: 2, label: 'Lodges' },
    { id: 3, label: 'Albergues' },
    { id: 4, label: 'Apartamentos' },
    { id: 5, label: 'Hotel Boutique' },
    { id: 6, label: 'Campamentos' },
    { id: 7, label: 'Bungalows' },
    { id: 8, label: 'Villas' },
    { id: 9, label: 'Condominios' },
    { id: 10, label: 'Refugios' },
    { id: 11, label: 'Posadas' },
    { id: 12, label: 'Moteles' },
    { id: 13, label: 'Hostales' },
    { id: 14, label: 'Pensiones' },
    { id: 15, label: 'Cabañas' },
    { id: 16, label: 'Resorts' },
    { id: 17, label: 'Casas y Chalets' },
    { id: 18, label: 'Casas de Huéspedes' },
    { id: 19, label: 'Rural Vivencial' },
    { id: 20, label: 'Apart Hoteles' },
    { id: 21, label: 'Paradores' },
    { id: 22, label: 'Boat Hoteles' },
  ];
}

/** Deterministic label for unit tests. */
const TYPE_LABELS: Record<number, Record<string, string>> = {
  1: { es: 'Hoteles', en: 'Hotels', pt: 'Hotéis' },
  2: { es: 'Lodges', en: 'Lodges', pt: 'Lodges' },
  3: { es: 'Albergues', en: 'Hostels', pt: 'Albergues' },
  4: { es: 'Apartamentos', en: 'Apartments', pt: 'Apartamentos' },
  5: { es: 'Hotel Boutique', en: 'Boutique Hotel', pt: 'Hotel Boutique' },
  6: { es: 'Campamentos', en: 'Camps', pt: 'Acampamentos' },
  7: { es: 'Bungalows', en: 'Bungalows', pt: 'Bangalôs' },
  8: { es: 'Villas', en: 'Villas', pt: 'Villas' },
  9: { es: 'Condominios', en: 'Condominiums', pt: 'Condomínios' },
  10: { es: 'Refugios', en: 'Refuges', pt: 'Refúgios' },
  11: { es: 'Posadas', en: 'Inns', pt: 'Pousadas' },
  12: { es: 'Moteles', en: 'Motels', pt: 'Motéis' },
  13: { es: 'Hostales', en: 'Hostels', pt: 'Hostels' },
  14: { es: 'Pensiones', en: 'Guesthouses', pt: 'Pensões' },
  15: { es: 'Cabañas', en: 'Cabins', pt: 'Cabanas' },
  16: { es: 'Resorts', en: 'Resorts', pt: 'Resorts' },
  17: { es: 'Casas y Chalets', en: 'Houses & Chalets', pt: 'Casas e Chalés' },
  18: { es: 'Casas de Huéspedes', en: 'Guest Houses', pt: 'Casas de Hóspedes' },
  19: { es: 'Rural Vivencial', en: 'Rural Living', pt: 'Rural Vivencial' },
  20: { es: 'Apart Hoteles', en: 'Apartment Hotels', pt: 'Apart Hotéis' },
  21: { es: 'Paradores', en: 'Paradores', pt: 'Paradores' },
  22: { es: 'Boat Hoteles', en: 'Boat Hotels', pt: 'Boat Hotéis' },
};

export function getHotelTypeLabel(id: number, locale = 'es'): string {
  return TYPE_LABELS[id]?.[locale] ?? String(id);
}
